<template>
  <validation-observer
    v-slot="propertyAssumptionsObserver"
    key="assumptions"
    slim
  >
    <form-section
      id="property-assumptions"
      v-bind="propertyAssumptionsObserver"
      :validated="formObserver.validated"
    >
      <form-row
        class="mb-2 assumptions-helptext"
        :label="title || 'Assumptions about your property'"
        :error="formObserver.validated && !propertyAssumptionsObserver.valid"
      >
        <template #helpText>
          <p>
            Please check the following assumptions are correct for your property.
          </p>
        </template>
      </form-row>

      <!-- EXPANSION PANELS -->
      <v-expansion-panels
        ref="assumptionPanels"
        v-model="panelsModel"
        class="base-restyle"
        multiple
        flat
      >
        <template v-for="(panel, index) in panels">
          <validation-observer
            :key="index"
            slim
          >
            <v-expansion-panel
              active-class="blue lighten-5"
            >
              <v-expansion-panel-header :id="panel.questions[0].questionId">
                <template #actions>
                  <v-icon color="primary">
                    $expand
                  </v-icon>
                </template>
                <template #default="{ open }">
                  <div
                    class="d-flex align-center justify-space-between"
                    :class="{ 'control-text': !open }"
                  >
                    <span
                      :class="{ 'font-weight-medium': !open }"
                      v-html="panel.preview"
                    />
                    <span
                      class="text-caption primary--text text-decoration-underline mr-2"
                    >
                      {{ open ? 'Close' : 'Edit' }}
                    </span>
                  </div>
                </template>
              </v-expansion-panel-header>

              <v-expansion-panel-content
                class="px-8 pb-4"
                eager
              >
                <template v-for="({question, ...rest}, qIndex) in panel.questions">
                  <component
                    :is="question"
                    :key="`${index}-${qIndex}`"

                    product-name="home"
                    step-id="home"
                    :form-observer="formObserver"
                    :form-data="formData"
                    :step-data="stepData"

                    v-bind="rest"

                    @input="onInput"
                  />
                </template>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </validation-observer>

          <v-divider
            :key="`divider-${index}`"
            class="divider"
          />
        </template>
      </v-expansion-panels>

      <!-- ASSUMPTIONS CONFIRM -->
      <form-row
        id="property-assumptions-confirm"
        label="Are the statements above correct?"
        class="mt-12 property-assumptions-confirm"
      >
        <validation-provider
          v-slot="{ errors, failed }"
          name="Property Assumptions Confirm"
          :vid="`${stepId}_is-assumptions-correct`"
          :rules="{propertyAssumptions: { other: true}}"
          slim
        >
          <radio
            v-model="isAssumptionsCorrect"
            name="property-assumptions-confirm"
            wide
            :error="failed"
            :items="constants.YES_NO"
            data-test="assumptions"
          />
          <div
            v-if="failed"
            class="error--text mt-4 fz12"
          >
            {{ errors[0] }}
          </div>
        </validation-provider>
      </form-row>
    </form-section>
  </validation-observer>
</template>

<script>
import useHomeAssumptions from '@/composables/useHomeAssumptions';

import QuestionBase from '@/components/questions/Base.vue';
import BaseRadioList from '@/components/questions/BaseRadioList';
import BaseRadioBoolean from '@/components/questions/BaseRadioBoolean';
import BaseDropdown from '@/components/questions/BaseDropdown';

import {
  lowerCase,
} from 'lodash';

export default {
  name: 'question-property-assumptions',
  extends: QuestionBase,
  components: {
    BaseRadioList,
    BaseRadioBoolean,
    BaseDropdown,
  },
  data () {
    return {
      fields: [
        'propertyWallsMade',
        'propertyMainResidence',
        'propertyLandslipHeave',
        'propertyUnderpinning',
        'propertyFlooding',
        'propertyWork',
        'propertyNearestRiver',
        'propertyListed',
        'propertyStateOfRepair',
        'propertyNearestTree',
      ],

      isAssumptionsCorrect: true,

      panels: null,
      panelsModel: [],
    };
  },
  watch: {
    isAssumptionsCorrect (val) {
      if (!val) {
        this.openExpansionPanels();
        this.$scrollTo('#property-assumptions');
      } else {
        this.panelsModel = [];
      }
    },
  },
  created () {
    const vm = this;

    this.panels = [
      {
        get preview () { return vm.wallsMade; },
        questions: [
          {
            question: 'question-base-radio-list',
            questionId: 'property-walls-made',
            title: 'What are the exterior walls made of?',
            customFields: { field: 'propertyWallsMade' },
            defaultValues: {
              field: 'BR',
            },
            otherOptionSlot: 'default',
            class: 'radio-list-other-opt-fix',
            optionsConfig: {
              placeholder: 'Please select walls material',
              mainItems: this.constants.HOME_PROPERTY_WALLS_MADE_FAVORITES,
              options: this.constants.HOME_PROPERTY_WALLS_MADE,
            },
            helpText: 'Please choose the material that the majority of the walls are made from.',
          },
        ],
      },
      {
        get preview () { return vm.landslipHeave; },
        questions: [
          {
            question: 'question-base-radio-boolean',
            questionId: 'property-landslip-heave',
            title: 'Is there a history/signs of heave, landslip or subsidence in the property OR in the area where the property is located?',
            customFields: { field: 'propertyLandslipHeave' },
            defaultValues: {
              field: false,
            },
            helpText: 'If this is the case, it will be detailed in your mortgage valuation or home buyers survey.',
          },
        ],
      },
      {
        get preview () { return vm.underPinning; },
        questions: [
          {
            question: 'question-base-radio-boolean',
            questionId: 'property-underpinning',
            title: 'Has the property ever had underpinning or structural support?',
            customFields: { field: 'propertyUnderpinning' },
            defaultValues: {
              field: false,
            },
            helpText: 'This information can be found in your Homebuyers report or mortgage valuation.',
          },
        ],
      },
      {
        get preview () { return vm.flooding; },
        questions: [
          {
            question: 'question-base-radio-boolean',
            questionId: 'property-flooding',
            title: 'Has the property got a history of flooding or is it located in an area which has suffered from flooding?',
            customFields: { field: 'propertyFlooding' },
            defaultValues: {
              field: false,
            },
            helpText: 'Has the property or surrounding area ever been flooded from an outside source, such as from a river or other watercourse? This does not include flooding caused by burst pipes or leaking water storage tanks.',
          },
        ],
      },
      {
        get preview () { return vm.work; },
        questions: [
          {
            question: 'question-base-radio-boolean',
            questionId: 'property-work',
            title: 'Is there any building work in progress?',
            customFields: { field: 'propertyWork' },
            defaultValues: {
              field: false,
            },
            helpText: 'If your home is currently undergoing building work, it may be more likely to suffer damage, so insurance providers are keen to know about it.',
          },
        ],
      },
      {
        get preview () { return vm.nearestRiver; },
        questions: [
          {
            question: 'question-base-radio-list',
            questionId: 'property-nearest-river',
            title: 'How far away is the nearest river or watercourse?',
            customFields: { field: 'propertyNearestRiver' },
            defaultValues: {
              field: 400,
            },
            helpText: 'If you are unsure, check with the Environment Agency.',
            optionsConfig: {
              options: this.constants.HOME_PROPERTY_NEAREST_RIVER,
            },
          },
        ],
      },
      {
        get preview () { return vm.listed; },
        questions: [
          {
            question: 'question-base-radio-list',
            questionId: 'property-listed',
            title: 'Is your property a listed building?',
            customFields: { field: 'propertyListed' },
            defaultValues: {
              field: 'N',
            },
            helpText: 'This means your property is considered to be of significant historic interest by your country\'s national heritage organisation.',
            optionsConfig: {
              options: this.constants.HOME_PROPERTY_LISTED,
            },
          },
        ],
      },
      {
        get preview () { return vm.mainResidence; },
        questions: [
          {
            question: 'question-base-radio-list',
            questionId: 'property-main-residence',
            title: 'Is this property the proposer\'s main residence?',
            customFields: { field: 'propertyMainResidence' },
            defaultValues: {
              field: 'Y',
            },
            helpText: 'Your main residence where you live most of the time.',
            optionsConfig: {
              options: this.constants.HOME_PROPERTY_MAIN_RESIDENCE,
            },
          },
        ],
      },
      {
        get preview () { return vm.stateOfRepair; },
        questions: [
          {
            question: 'question-base-radio-boolean',
            questionId: 'property-state-of-repair',
            title: 'Is the property in good state of repair?',
            customFields: { field: 'propertyStateOfRepair' },
            defaultValues: {
              field: true,
            },
            helpText: 'Insurance providers need to know about anything that could cause a claim if your home isn\'t structurally sound - for example rot, damp, mildew, leaks, faulty wiring or plumbing.',
          },
        ],
      },
      {
        get preview () { return vm.nearestTree; },
        questions: [
          {
            question: 'question-base-radio-boolean',
            questionId: 'property-nearest-tree',
            title: 'Are there trees taller than 10 metres within 5 metres of your building?',
            customFields: { field: 'propertyNearestTree' },
            defaultValues: {
              field: false,
            },
            helpText: 'If the trees  are taller than a 3 storey house then they\'re likely to be taller than 10 metres (33 ft). Large trees and roots close to your property could cause damage.',
          },
        ],
      },
    ];
  },
  methods: {
    onInput (e) {
      this.questionData = { ...this.questionData, ...e };
    },

    openExpansionPanels () {
      this.panelsModel = [...Array(this.panels.length).keys()];
    },
  },
  setup (props) {
    return {
      lowerCase,
      ...useHomeAssumptions(props),
    };
  },
};
</script>

<style lang='scss'>
.assumptions-helptext {
  .form-col {
    width: 100% !important;
  }
}

.v-expansion-panel {
  padding: 8px 13px;
}

.divider {
  flex-basis: 100%;
}

.control-text .notice {
  color: var(--v-primary-base) !important;
  caret-color: var(--v-primary-base) !important;
}

.base-restyle {
  .form-section {
    background-color: inherit;
  }

  .form-section__container,
  .form-section__container-holder {
    padding: 0 !important;
  }

  .form-section__validity {
    display: none;
  }

  .radio-holder.wide-row {
    display: block;
  }

  .radio-list-other-opt-fix {
    .radio-list-other {
      .form-col:first-of-type {
        display: none;
      }

      .form-row__content {
        width: 100%;
        margin-top: var(--gap, 24px);
      }
    }
  }
}
</style>
